import React, { useEffect } from 'react'

interface ToastProps {
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  onDismiss: () => void
  /** Auto-dismiss delay in ms. Default: 5000 */
  duration?: number
}

const TYPE_STYLES: Record<NonNullable<ToastProps['type']>, string> = {
  info:    'bg-gray-800 text-white',
  success: 'bg-green-600 text-white',
  warning: 'bg-amber-600 text-white',
  error:   'bg-red-600 text-white',
}

/**
 * Fixed-bottom toast notification. Auto-dismisses after `duration` ms.
 * Replaces native alert() calls so we keep the PWA feel on mobile.
 *
 * Usage in a page component:
 *   const [toast, setToast] = useState<{ message: string; type: 'warning' | 'error' | 'info' } | null>(null)
 *   // trigger:  setToast({ message: 'Algo correu mal', type: 'error' })
 *   // render:   {toast && <ToastNotification {...toast} onDismiss={() => setToast(null)} />}
 */
export const ToastNotification: React.FC<ToastProps> = ({
  message,
  type = 'info',
  onDismiss,
  duration = 5000,
}) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, duration)
    return () => clearTimeout(timer)
  }, [onDismiss, duration])

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-full max-w-sm px-4 pointer-events-none"
    >
      <div
        className={`${TYPE_STYLES[type]} rounded-xl shadow-xl px-4 py-3 flex items-start gap-3 pointer-events-auto`}
      >
        <span className="text-sm leading-snug flex-1">{message}</span>
        <button
          onClick={onDismiss}
          aria-label="Fechar notificação"
          className="text-white/70 hover:text-white text-base leading-none shrink-0 mt-0.5"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
